import {
  outputSchema,
  parseProject,
  uid,
  type OutputProfile,
  type Project,
} from "./schema";
import { productionSchema, edges, type Production } from "./production";
import { impositionSchema } from "./imposition";
import type { Resource } from "./catalog";

export const outputRoles = ["preview", "proof", "high-res", "print"] as const;
export type OutputRole = (typeof outputRoles)[number];
export const outputRoleLabels: Record<OutputRole, string> = {
  preview: "Preview / Proof",
  proof: "Proof Download",
  "high-res": "High Res",
  print: "Print",
};
export function createOutputProfile(role: OutputRole): OutputProfile {
  return outputSchema.parse({
    id: uid("output"),
    role,
    name: outputRoleLabels[role],
    format: role === "preview" ? "png" : "pdf",
    dpi: { preview: 96, proof: 72, "high-res": 300, print: 600 }[role],
    includeProduction: role === "high-res" || role === "print",
    ...(role === "proof" ? { rasterize: true } : {}),
  });
}
export const createOutputProfiles = () => outputRoles.map(createOutputProfile);
export const isProofOutput = (output: OutputProfile) =>
  output.role === "preview" || output.role === "proof";
export function configureOutputRole(output: OutputProfile, role: OutputRole) {
  const proof = role === "preview" || role === "proof";
  return outputSchema.parse({
    ...output,
    role,
    ...(proof
      ? {
          format: role === "preview" ? "png" : "pdf",
          colorSpace: "rgb",
          dpi: role === "preview" ? 96 : 72,
          rasterize: role === "proof",
          includeProduction: false,
          imposition: impositionSchema.parse({}),
          sheetProfile: undefined,
          printProfile: undefined,
          preserveImportedPdf: undefined,
          preserveImportedPaint: undefined,
        }
      : { rasterize: false }),
  });
}
/** Migration changes editable documents only. Historical snapshots stay untouched. */
export function migrateOutputProfiles(
  project: Project,
  force = false,
): Project {
  if (project.outputs.length <= 4 && !force) return project;
  const remaining = [...project.outputs],
    selected: OutputProfile[] = [];
  for (const role of outputRoles) {
    const match =
      remaining.find((o) => o.role === role) ||
      remaining.find((o) =>
        role === "preview"
          ? o.format === "png"
          : role === "proof"
            ? o.format === "pdf" &&
              (o.dpi <= 150 || /proof|low|\bLR\b/i.test(o.name))
            : role === "high-res"
              ? o.format === "pdf" && o.colorSpace === "rgb"
              : o.format === "pdf" && o.colorSpace === "cmyk",
      );
    if (match) remaining.splice(remaining.indexOf(match), 1);
    selected.push(
      match ? configureOutputRole(match, role) : createOutputProfile(role),
    );
  }
  const archived = [...(project.archivedOutputs || []), ...project.outputs];
  const presets = [...project.resourceSnapshots];
  for (const output of project.outputs) {
    if (
      !presets.some(
        (r) =>
          r.kind === "output" &&
          outputSettingsKey(r.value) === outputSettingsKey(output),
      )
    )
      presets.push({
        id: uid("resource"),
        tenantId: project.tenantId,
        kind: "output",
        name: output.name,
        version: 1,
        value: structuredClone(output),
      });
  }
  return parseProject({
    ...project,
    outputs: selected,
    archivedOutputs: archived,
    resourceSnapshots: presets,
  });
}
/** The on-screen proof is a logical product; sheet production is an export concern. */
export function previewOutput(
  output: OutputProfile,
  showBleed = false,
): OutputProfile {
  return {
    ...output,
    includeProduction: showBleed,
    imposition: { ...output.imposition, enabled: false },
  };
}
export function productionKey(input: Production): string {
  const value = productionSchema.parse(input);
  const normalize = (v: unknown): unknown =>
    typeof v === "number"
      ? Math.round(v * 10000) / 10000
      : v && typeof v === "object"
        ? Object.fromEntries(
            Object.entries(v)
              .filter(([, x]) => x !== undefined)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([k, x]) => [k, normalize(x)]),
          )
        : v;
  return JSON.stringify(
    normalize({ ...value, bleed: edges(value.bleed), slug: edges(value.slug) }),
  );
}
export function outputSettingsKey(input: OutputProfile): string {
  const {
    id,
    name,
    role,
    outputPreset,
    productionProfile,
    sheetProfile,
    ...settings
  } = outputSchema.parse(input);
  const canonical = (value: unknown): unknown =>
    Array.isArray(value)
      ? value.map(canonical)
      : value && typeof value === "object"
        ? Object.fromEntries(
            Object.entries(value)
              .filter(([, v]) => v !== undefined)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([k, v]) => [k, canonical(v)]),
          )
        : value;
  return JSON.stringify(
    canonical({
      ...settings,
      rasterize: settings.rasterize || undefined,
      includeProduction: settings.includeProduction !== false,
    }),
  );
}
/** A global definition owns its settings, never another document's preset links. */
export function globalOutputSettings(input: OutputProfile): OutputProfile {
  const { outputPreset, productionProfile, sheetProfile, ...settings } =
    outputSchema.parse(input);
  return outputSchema.parse(settings);
}
export async function retainOutputPresets(
  project: Project,
  available: Resource[],
  save: (resource: Resource) => Promise<Resource>,
): Promise<Project> {
  const resources = [...available];
  const snapshots = [...project.resourceSnapshots];
  const references = new Map<
    string,
    { id: string; version: number; name: string }
  >();
  for (let index = 0; index < snapshots.length; index++) {
    const snapshot = snapshots[index];
    if (snapshot.kind !== "output") continue;
    let resource = resources.find(
      (r) =>
        r.tenantId === project.tenantId &&
        !r.projectId &&
        r.kind === "output" &&
        !r.archived &&
        (r.value as OutputProfile).role === snapshot.value.role &&
        outputSettingsKey(r.value as OutputProfile) ===
          outputSettingsKey(snapshot.value),
    );
    if (!resource) {
      resource = await save({
        ...snapshot,
        id: resources.some((r) => r.id === snapshot.id)
          ? uid("resource")
          : snapshot.id,
        projectId: undefined,
        version: 0,
        value: globalOutputSettings(snapshot.value),
      });
      resources.push(resource);
    }
    references.set(`${snapshot.id}:${snapshot.version}`, {
      id: resource.id,
      version: resource.version,
      name: resource.name,
    });
    snapshots[index] = resource;
  }
  return parseProject({
    ...project,
    outputs: project.outputs.map((output) => ({
      ...output,
      ...(output.outputPreset &&
      references.has(`${output.outputPreset.id}:${output.outputPreset.version}`)
        ? {
            outputPreset: references.get(
              `${output.outputPreset.id}:${output.outputPreset.version}`,
            ),
          }
        : {}),
    })),
    resourceSnapshots: snapshots.filter(
      (r, i, all) =>
        !all
          .slice(0, i)
          .some((other) => other.id === r.id && other.version === r.version),
    ),
  });
}
export async function retainProductionPresets(
  project: Project,
  available: Resource[],
  save: (resource: Resource) => Promise<Resource>,
): Promise<Project> {
  const resources = [...available];
  const references = new Map<
    string,
    { id: string; name: string; version: number }
  >();
  const snapshots = [];
  for (const snapshot of project.resourceSnapshots) {
    if (snapshot.kind !== "production") {
      snapshots.push(snapshot);
      continue;
    }
    let row = resources.find(
      (candidate) =>
        candidate.kind === "production" &&
        candidate.tenantId === project.tenantId &&
        candidate.projectId === project.id &&
        !candidate.archived &&
        productionKey(candidate.value as Production) ===
          productionKey(snapshot.value),
    );
    if (!row) {
      row = await save({
        ...snapshot,
        id: resources.some((candidate) => candidate.id === snapshot.id)
          ? uid("resource")
          : snapshot.id,
        projectId: project.id,
        version: 0,
      });
      resources.push(row);
    }
    references.set(`${snapshot.id}:${snapshot.version}`, {
      id: row.id,
      version: row.version,
      name: row.name,
    });
    if (
      !snapshots.some(
        (candidate) =>
          candidate.id === row.id && candidate.version === row.version,
      )
    )
      snapshots.push(row);
  }
  return parseProject({
    ...project,
    resourceSnapshots: snapshots,
    outputs: project.outputs.map((output) =>
      output.productionProfile &&
      references.has(
        `${output.productionProfile.id}:${output.productionProfile.version}`,
      )
        ? {
            ...output,
            productionProfile: references.get(
              `${output.productionProfile.id}:${output.productionProfile.version}`,
            ),
          }
        : output,
    ),
  });
}
export function assignOutputResource(
  project: Project,
  outputId: string,
  resource: Resource,
): Project {
  if (
    resource.tenantId !== project.tenantId ||
    resource.archived ||
    (resource.projectId && resource.projectId !== project.id)
  )
    throw new Error("Profile is unavailable");
  const selected = project.outputs.find((o) => o.id === outputId);
  if (!selected) throw new Error("Output profile not found");
  if (resource.kind === "output") {
    const definition = outputSchema.parse(resource.value);
    if (definition.role && selected.role && definition.role !== selected.role)
      throw new Error(
        `Assign this profile to the ${outputRoleLabels[definition.role]} stage`,
      );
  }
  const reference = {
    id: resource.id,
    name: resource.name,
    version: resource.version,
  };
  const patch =
    resource.kind === "output"
      ? {
          ...outputSchema.parse(resource.value),
          id: selected.id,
          role: selected.role,
          outputPreset: reference,
          ...(selected.role === "proof" ? { rasterize: true } : {}),
        }
      : resource.kind === "production"
        ? {
            production: productionSchema.parse(resource.value),
            productionProfile: reference,
          }
        : resource.kind === "sheet"
          ? {
              imposition: impositionSchema.parse(resource.value),
              sheetProfile: reference,
            }
          : undefined;
  if (!patch) throw new Error("Select an output, bleed or sheet profile");
  if (resource.kind === "sheet" && isProofOutput(selected))
    throw new Error("Proof outputs use individual product pages");
  return parseProject({
    ...project,
    outputs: project.outputs.map((o) =>
      o.id === outputId
        ? resource.kind === "output"
          ? outputSchema.parse(patch)
          : { ...o, ...patch }
        : o,
    ),
    resourceSnapshots: [
      ...project.resourceSnapshots.filter(
        (r) => r.id !== resource.id || r.version !== resource.version,
      ),
      resource,
    ],
  });
}
